import { RouteObject, Navigate } from 'react-router'
import reactLazyWithRetry from '@fatso83/retry-dynamic-import/react-lazy'
import ActivateAccount from 'pages/AccountManagement/ActivateAccount'
import ForgotPassword from 'pages/AccountManagement/ForgotPassword'
import Login from 'pages/AccountManagement/Login'
import TermsAndPolicy from 'pages/AccountManagement/TermsAndPolicy/TermsAndPolicy'
import Logout from 'pages/AccountManagement/Logout'
import ResetPassword from 'pages/AccountManagement/ResetPassword'
import VerifyAccount from 'pages/AccountManagement/VerifyAccount'
import HttpErrorPage from 'Components/General/HttpErrorPage'
import Unsubscribe from 'pages/AccountManagement/Unsubscribe'
import CVPrint from 'pages/CVPrint'
import Notifications from 'pages/Notifications'
import Profile from 'pages/Profile'
import Search from 'pages/Search/index'
import ReleaseNotes from 'pages/ReleaseNotes'
import Team from 'pages/Team'
import Allocation from 'pages/Allocation'
import ProtectedRoute from './ProtectedRoute'
import Layout from './Layout'
import { PrivateRoute } from './routeGuards'

const Admin = reactLazyWithRetry(() => import('pages/Admin'))
const CompanyAdmin = reactLazyWithRetry(() => import('pages/CompanyAdmin'))
const Assignments = reactLazyWithRetry(() => import('pages/Assignments'))
const AssignmentAddEdit = reactLazyWithRetry(
  () => import('pages/Assignments/AssignmentsList/AddEdit/AssignmentAddEdit')
)
const AssignmentDetails = reactLazyWithRetry(() => import('pages/Assignments/AssignmentDetails'))
const AssignmentProposed = reactLazyWithRetry(() => import('pages/Assignments/AssignmentProposals/RoleCandidates/'))
const ProposeToRole = reactLazyWithRetry(() => import('pages/Assignments/AssignmentProposals/AddEdit/ProposeToRole'))
const AssignmentsView = reactLazyWithRetry(() => import('pages/Assignments/AssignmentsView'))
const Networks = reactLazyWithRetry(() => import('pages/Networks'))
const NetworkEdit = reactLazyWithRetry(() => import('pages/Networks/NetworkAddEdit/NetworkAddEdit'))
const NetworkManage = reactLazyWithRetry(() => import('pages/Networks/NetworkManage'))
const NetworkBenefits = reactLazyWithRetry(() => import('pages/Networks/NetworkBenefits/NetworkBenefits'))
const SalesPoolView = reactLazyWithRetry(() => import('pages/SalesPool/SalesPoolView'))
const SalesPoolPreview = reactLazyWithRetry(() => import('pages/SalesPool/SalesPoolPreview/SalesPoolPreview'))
const Scout = reactLazyWithRetry(() => import('pages/Scout'))
const Statistics = reactLazyWithRetry(() => import('pages/Statistics'))
const UserManual = reactLazyWithRetry(() => import('pages/UserManual'))
const SalesPool = reactLazyWithRetry(() => import('pages/SalesPool'))
const Chat = reactLazyWithRetry(() => import('pages/Chat'))
const Room = reactLazyWithRetry(() => import('pages/Chat/Room'))
const CvWizard = reactLazyWithRetry(() => import('pages/Profile/CvWizard'))

export const routes: RouteObject[] = [
  {
    id: 'root',
    element: <Layout />,
    children: [
      {
        path: 'login',
        element: <Login />,
      },
      { path: 'terms', element: <TermsAndPolicy /> },
      { path: 'privacy-policy', element: <TermsAndPolicy /> },
      { path: 'activate', element: <ActivateAccount /> },
      { path: 'forgot-password', element: <ForgotPassword /> },
      { path: 'change-password/:token', element: <ResetPassword /> },
      { path: 'logout', element: <Logout /> },
      { path: '404', element: <HttpErrorPage type={'404'} /> },
      { path: '403', element: <HttpErrorPage type={'403'} /> },
      { path: 'confirm/:token', element: <VerifyAccount /> },
      {
        path: 'assignments/public/:publicId',
        element: <AssignmentsView />,
      },
      {
        path: 'workorders/public/:publicId',
        element: <AssignmentsView />,
      },
      {
        path: 'salespool/:publicId',
        element: <SalesPoolView />,
      },
      {
        path: 'salespool/item/:publicId',
        element: <SalesPoolPreview />,
      },
      {
        path: '/unsubscribe',
        element: <Unsubscribe />,
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: 'salespool',
            element: (
              <PrivateRoute path="/salespool">
                <SalesPool />
              </PrivateRoute>
            ),
          },
          {
            path: 'profile',
            children: [
              {
                path: '',
                element: (
                  <PrivateRoute path="/profile">
                    <Profile />
                  </PrivateRoute>
                ),
              },
              {
                path: ':profileId',
                element: (
                  <PrivateRoute path="/profile/:profileId">
                    <Profile />
                  </PrivateRoute>
                ),
              },
              {
                path: ':profileId/wizard/:cvId/:step?',
                element: (
                  <PrivateRoute path="/profile/:profileId/wizard">
                    <CvWizard />
                  </PrivateRoute>
                ),
              },
            ],
          },
          {
            path: 'notifications',
            element: (
              <PrivateRoute path="/notifications">
                <Notifications />
              </PrivateRoute>
            ),
          },
          {
            path: 'employees',
            element: (
              <PrivateRoute path="/employees">
                <Search />
              </PrivateRoute>
            ),
          },
          {
            path: 'teams',
            children: [
              {
                path: '',
                element: (
                  <PrivateRoute path="/teams">
                    <Team />
                  </PrivateRoute>
                ),
              },
              {
                path: ':id',
                element: (
                  <PrivateRoute path="/teams/:id">
                    <Team />
                  </PrivateRoute>
                ),
              },
              {
                path: 'add',
                element: (
                  <PrivateRoute path="/teams/add">
                    <Team />
                  </PrivateRoute>
                ),
              },
              {
                path: 'edit/:id',
                element: (
                  <PrivateRoute path="/teams/edit/:id">
                    <Team />
                  </PrivateRoute>
                ),
              },
            ],
          },
          {
            path: 'assignments',
            children: [
              {
                path: '',
                element: (
                  <PrivateRoute path="/assignments">
                    <Assignments />
                  </PrivateRoute>
                ),
              },
              /*{
                path: 'proposal-search',
                element: (
                  <PrivateRoute path="/assignments/proposal-search">
                    <Assignments />
                  </PrivateRoute>
                ),
              },*/
              {
                path: 'proposals/:proposalId',
                element: (
                  <PrivateRoute path="/assignments/proposals/:proposalId">
                    <Assignments />
                  </PrivateRoute>
                ),
              },
              {
                path: 'proposals',
                element: (
                  <PrivateRoute path="/assignments/proposals">
                    <Assignments />
                  </PrivateRoute>
                ),
              },
              {
                path: 'add',
                element: (
                  <PrivateRoute path="/assignments/add">
                    <AssignmentAddEdit />
                  </PrivateRoute>
                ),
              },
              {
                path: ':assignmentId',
                element: (
                  <PrivateRoute path="/assignments/:assignmentId">
                    <AssignmentDetails />
                  </PrivateRoute>
                ),
              },
              {
                path: 'edit/:id',
                element: (
                  <PrivateRoute path="/assignments/edit/:id">
                    <AssignmentAddEdit />
                  </PrivateRoute>
                ),
              },
              {
                path: 'edit',
                element: (
                  <PrivateRoute path="/assignments/edit">
                    <AssignmentAddEdit />
                  </PrivateRoute>
                ),
              },
              {
                path: 'propose/:roleId',
                element: (
                  <PrivateRoute path="/assignments/propose/:roleId">
                    <ProposeToRole />
                  </PrivateRoute>
                ),
              },
              {
                path: 'addpropose/:roleId',
                element: (
                  <PrivateRoute path="/assignments/addpropose/:roleId">
                    <ProposeToRole />
                  </PrivateRoute>
                ),
              },
              {
                path: 'proposal/edit/:proposalId',
                element: (
                  <PrivateRoute path="/assignments/proposal/edit/:proposalId">
                    <ProposeToRole />
                  </PrivateRoute>
                ),
              },
              {
                path: 'copy/:id',
                element: (
                  <PrivateRoute path="/assignments/copy/:id">
                    <AssignmentAddEdit />
                  </PrivateRoute>
                ),
              },
              {
                path: 'copy/share/:id',
                element: (
                  <PrivateRoute path="/assignments/copy/share/:id">
                    <AssignmentAddEdit />
                  </PrivateRoute>
                ),
              },
              {
                path: 'proposed/:id',
                element: (
                  <PrivateRoute path="/assignments/proposed/:id">
                    <AssignmentProposed />
                  </PrivateRoute>
                ),
              },
              {
                path: 'responses/:responseId',
                element: (
                  <PrivateRoute path="/assignments/responses/:responseId">
                    <Assignments />
                  </PrivateRoute>
                ),
              },
              {
                path: 'responses',
                element: (
                  <PrivateRoute path="/assignments/responses">
                    <Assignments />
                  </PrivateRoute>
                ),
              },
              {
                path: 'archived',
                element: (
                  <PrivateRoute path="/assignments/archived">
                    <Assignments />
                  </PrivateRoute>
                ),
              },
            ],
          },
          {
            path: 'workorders',
            children: [
              {
                path: '',
                element: (
                  <PrivateRoute path="/workorders">
                    <Assignments />
                  </PrivateRoute>
                ),
              },
              /*{
                path: 'proposal-search',
                element: (
                  <PrivateRoute path="/workorders/proposal-search">
                    <Assignments />
                  </PrivateRoute>
                ),
              },*/
              {
                path: 'proposals/:proposalId',
                element: (
                  <PrivateRoute path="/workorders/proposals/:proposalId">
                    <Assignments />
                  </PrivateRoute>
                ),
              },
              {
                path: 'proposals',
                element: (
                  <PrivateRoute path="/workorders/proposals">
                    <Assignments />
                  </PrivateRoute>
                ),
              },
              {
                path: 'add',
                element: (
                  <PrivateRoute path="/workorders/add">
                    <AssignmentAddEdit />
                  </PrivateRoute>
                ),
              },
              {
                path: ':assignmentId',
                element: (
                  <PrivateRoute path="/workorders/:assignmentId">
                    <AssignmentDetails />
                  </PrivateRoute>
                ),
              },
              {
                path: 'edit/:id',
                element: (
                  <PrivateRoute path="/workorders/edit/:id">
                    <AssignmentAddEdit />
                  </PrivateRoute>
                ),
              },
              {
                path: 'edit',
                element: (
                  <PrivateRoute path="/workorders/edit">
                    <AssignmentAddEdit />
                  </PrivateRoute>
                ),
              },
              {
                path: 'propose/:roleId',
                element: (
                  <PrivateRoute path="/workorders/propose/:roleId">
                    <ProposeToRole />
                  </PrivateRoute>
                ),
              },
              {
                path: 'addpropose/:roleId',
                element: (
                  <PrivateRoute path="/workorders/addpropose/:roleId">
                    <ProposeToRole />
                  </PrivateRoute>
                ),
              },
              {
                path: 'proposal/edit/:proposalId',
                element: (
                  <PrivateRoute path="/workorders/proposal/edit/:proposalId">
                    <ProposeToRole />
                  </PrivateRoute>
                ),
              },
              {
                path: 'copy/:id',
                element: (
                  <PrivateRoute path="/workorders/copy/:id">
                    <AssignmentAddEdit />
                  </PrivateRoute>
                ),
              },
              {
                path: 'copy/share/:id',
                element: (
                  <PrivateRoute path="/workorders/copy/share/:id">
                    <AssignmentAddEdit />
                  </PrivateRoute>
                ),
              },
              {
                path: 'proposed/:id',
                element: (
                  <PrivateRoute path="/workorders/proposed/:id">
                    <AssignmentProposed />
                  </PrivateRoute>
                ),
              },
              {
                path: 'responses/:responseId',
                element: (
                  <PrivateRoute path="/workorders/responses/:responseId">
                    <Assignments />
                  </PrivateRoute>
                ),
              },
              {
                path: 'responses',
                element: (
                  <PrivateRoute path="/workorders/responses">
                    <Assignments />
                  </PrivateRoute>
                ),
              },
              {
                path: 'archived',
                element: (
                  <PrivateRoute path="/workorders/archived">
                    <Assignments />
                  </PrivateRoute>
                ),
              },
            ],
          },
          {
            path: 'admin',
            children: [
              {
                path: '',
                element: (
                  <PrivateRoute path="/admin">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'employees/:employeeId',
                element: (
                  <PrivateRoute path="/admin/employees/:employeeId">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'employees',
                element: (
                  <PrivateRoute path="/admin/employees">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'organizations/:organizationId',
                element: (
                  <PrivateRoute path="/admin/organizations/:organizationId">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'organizations',
                element: (
                  <PrivateRoute path="/admin/organizations">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'permissions',
                element: (
                  <PrivateRoute path="/admin/permissions">
                    <Admin />
                  </PrivateRoute>
                ),
                children: [
                  {
                    path: 'groups/:groupId',
                    element: (
                      <PrivateRoute path="/admin/permissions/groups/:groupId">
                        <Admin />
                      </PrivateRoute>
                    ),
                  },
                  {
                    path: 'groups',
                    element: (
                      <PrivateRoute path="/admin/permissions/groups">
                        <Admin />
                      </PrivateRoute>
                    ),
                  },
                  {
                    path: 'users/:userId',
                    element: (
                      <PrivateRoute path="/admin/permissions/users/:userId">
                        <Admin />
                      </PrivateRoute>
                    ),
                  },
                  {
                    path: 'users',
                    element: (
                      <PrivateRoute path="/admin/permissions/users">
                        <Admin />
                      </PrivateRoute>
                    ),
                  },
                ],
              },
              {
                path: 'features',
                element: (
                  <PrivateRoute path="/admin/features">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'data-management',
                element: (
                  <PrivateRoute path="/admin/data-management">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'user-manual',
                element: (
                  <PrivateRoute path="/admin/user-manual">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'notifications',
                element: (
                  <PrivateRoute path="/admin/notifications">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'active-users',
                element: (
                  <PrivateRoute path="/admin/active-users">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'parse-cv',
                element: (
                  <PrivateRoute path="/admin/parse-cv">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'parse-statistics',
                element: (
                  <PrivateRoute path="/admin/parse-statistics">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'audit-log',
                element: (
                  <PrivateRoute path="/admin/audit-log">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'release-notes',
                element: (
                  <PrivateRoute path="/admin/release-notes">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'system-configs',
                element: (
                  <PrivateRoute path="/admin/system-configs">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'cache-management',
                element: (
                  <PrivateRoute path="/admin/cache-management">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'test-data',
                element: (
                  <PrivateRoute path="/admin/test-data">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'search',
                element: (
                  <PrivateRoute path="/admin/search">
                    <Admin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'maintenance',
                element: (
                  <PrivateRoute path="/admin/maintenance">
                    <Admin />
                  </PrivateRoute>
                ),
              },
            ],
          },
          {
            path: 'company-admin',
            children: [
              {
                path: '',
                element: (
                  <PrivateRoute path="/company-admin">
                    <CompanyAdmin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'employees/:employeeId',
                element: (
                  <PrivateRoute path="/company-admin/employees/:employeeId">
                    <CompanyAdmin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'employees',
                element: (
                  <PrivateRoute path="/company-admin/employees">
                    <CompanyAdmin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'organizations/:organizationId',
                element: (
                  <PrivateRoute path="/company-admin/organizations/:organizationId">
                    <CompanyAdmin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'organizations',
                element: (
                  <PrivateRoute path="/company-admin/organizations">
                    <CompanyAdmin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'permissions',
                element: (
                  <PrivateRoute path="/company-admin/permissions">
                    <CompanyAdmin />
                  </PrivateRoute>
                ),
                children: [
                  {
                    path: 'groups/:groupId',
                    element: (
                      <PrivateRoute path="/company-admin/permissions/groups/:groupId">
                        <CompanyAdmin />
                      </PrivateRoute>
                    ),
                  },
                  {
                    path: 'groups',
                    element: (
                      <PrivateRoute path="/company-admin/permissions/groups">
                        <CompanyAdmin />
                      </PrivateRoute>
                    ),
                  },
                  {
                    path: 'users/:userId',
                    element: (
                      <PrivateRoute path="/company-admin/permissions/users/:userId">
                        <CompanyAdmin />
                      </PrivateRoute>
                    ),
                  },
                  {
                    path: 'users',
                    element: (
                      <PrivateRoute path="/company-admin/permissions/users">
                        <CompanyAdmin />
                      </PrivateRoute>
                    ),
                  },
                ],
              },
              {
                path: 'networks',
                element: (
                  <PrivateRoute path="/company-admin/networks">
                    <CompanyAdmin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'parse',
                element: (
                  <PrivateRoute path="/company-admin/parse">
                    <CompanyAdmin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'widgets',
                element: (
                  <PrivateRoute path="/company-admin/widgets">
                    <CompanyAdmin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'units',
                element: (
                  <PrivateRoute path="/company-admin/units">
                    <CompanyAdmin />
                  </PrivateRoute>
                ),
              },
              {
                path: 'note-tags',
                element: (
                  <PrivateRoute path="/company-admin/note-tags">
                    <CompanyAdmin />
                  </PrivateRoute>
                ),
              },
            ],
          },
          {
            path: 'allocation',
            children: [
              {
                path: '',
                element: (
                  <PrivateRoute path="/allocation">
                    <Allocation />
                  </PrivateRoute>
                ),
              },
              {
                path: ':profileId',
                element: (
                  <PrivateRoute path="/allocation/:profileId">
                    <Allocation />
                  </PrivateRoute>
                ),
              },
              {
                path: 'suppliers',
                element: (
                  <PrivateRoute path="/allocation/suppliers">
                    <Allocation />
                  </PrivateRoute>
                ),
              },
              {
                path: 'overview',
                element: (
                  <PrivateRoute path="/allocation/overview">
                    <Allocation />
                  </PrivateRoute>
                ),
              },
            ],
          },
          {
            path: 'print',
            children: [
              {
                path: '',
                element: (
                  <PrivateRoute path="/print">
                    <CVPrint />
                  </PrivateRoute>
                ),
              },
              {
                path: ':cvId',
                element: (
                  <PrivateRoute path="/print/:cvId">
                    <CVPrint />
                  </PrivateRoute>
                ),
              },
            ],
          },
          {
            path: 'scout',
            element: (
              <PrivateRoute path="/scout">
                <Scout />
              </PrivateRoute>
            ),
          },
          {
            path: 'networks',
            children: [
              {
                path: '',
                element: (
                  <PrivateRoute path="/networks">
                    <Networks />
                  </PrivateRoute>
                ),
              },
              {
                path: 'manage/:id/notifications',
                element: (
                  <PrivateRoute path="/networks/manage/:id/notifications">
                    <NetworkManage />
                  </PrivateRoute>
                ),
              },
              {
                path: 'manage/:id/proposalinfo',
                element: (
                  <PrivateRoute path="/networks/manage/:id/proposalinfo">
                    <NetworkManage />
                  </PrivateRoute>
                ),
              },
              {
                path: 'manage/:id/infopage',
                element: (
                  <PrivateRoute path="/networks/manage/:id/infopage">
                    <NetworkManage />
                  </PrivateRoute>
                ),
              },
              {
                path: 'manage/:id/parse',
                element: (
                  <PrivateRoute path="/networks/manage/:id/parse">
                    <NetworkManage />
                  </PrivateRoute>
                ),
              },
              {
                path: 'manage/:id/organizations/:organizationId',
                element: (
                  <PrivateRoute path="/networks/manage/:id/organizations/:organizationId">
                    <NetworkManage />
                  </PrivateRoute>
                ),
              },
              {
                path: 'manage/:id/organizations',
                element: (
                  <PrivateRoute path="/networks/manage/:id/organizations">
                    <NetworkManage />
                  </PrivateRoute>
                ),
              },
              {
                path: 'manage/:id/employees/:employeeId',
                element: (
                  <PrivateRoute path="/networks/manage/:id/employees/:employeeId">
                    <NetworkManage />
                  </PrivateRoute>
                ),
              },
              {
                path: 'manage/:id/employees',
                element: (
                  <PrivateRoute path="/networks/manage/:id/employees">
                    <NetworkManage />
                  </PrivateRoute>
                ),
              },
              {
                path: 'manage/:id',
                element: (
                  <PrivateRoute path="/networks/manage/:id">
                    <NetworkManage />
                  </PrivateRoute>
                ),
              },
              {
                path: 'benefits',
                element: (
                  <PrivateRoute path="/networks/benefits">
                    <NetworkBenefits />
                  </PrivateRoute>
                ),
              },
              {
                path: 'add',
                element: (
                  <PrivateRoute path="/networks/add">
                    <NetworkEdit />
                  </PrivateRoute>
                ),
              },
              {
                path: ':id',
                element: (
                  <PrivateRoute path="/networks/:id">
                    <NetworkEdit />
                  </PrivateRoute>
                ),
              },
            ],
          },
          {
            path: 'help',
            children: [
              {
                path: '',
                element: (
                  <PrivateRoute path="/help">
                    <UserManual />
                  </PrivateRoute>
                ),
              },
              {
                path: ':url',
                element: (
                  <PrivateRoute path="/help/:url">
                    <UserManual />
                  </PrivateRoute>
                ),
              },
            ],
          },
          {
            path: 'statistics',
            children: [
              {
                path: '',
                element: (
                  <PrivateRoute path="/statistics">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'scout',
                element: (
                  <PrivateRoute path="/statistics/scout">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'scout-plus',
                element: (
                  <PrivateRoute path="/statistics/scout-plus">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'skills/totals',
                element: (
                  <PrivateRoute path="/statistics/skills/totals">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'skills/interest',
                element: (
                  <PrivateRoute path="/statistics/skills/interest">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'skills/level',
                element: (
                  <PrivateRoute path="/statistics/skills/level">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'skills/experience',
                element: (
                  <PrivateRoute path="/statistics/skills/experience">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'skills/education',
                element: (
                  <PrivateRoute path="/statistics/skills/education">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'skills/demand',
                element: (
                  <PrivateRoute path="/statistics/skills/demand">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'skills/learning-goals',
                element: (
                  <PrivateRoute path="/statistics/skills/learning-goals">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'certificates/users',
                element: (
                  <PrivateRoute path="/statistics/certificates/users">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'certificates/totals',
                element: (
                  <PrivateRoute path="/statistics/certificates/totals">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'certificates/count',
                element: (
                  <PrivateRoute path="/statistics/certificates/totals">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'cvs',
                element: (
                  <PrivateRoute path="/statistics/cvs">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'hitrate',
                element: (
                  <PrivateRoute path="/statistics/hitrate">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'suppliers',
                element: (
                  <PrivateRoute path="/statistics/suppliers">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
              {
                path: 'rating',
                element: (
                  <PrivateRoute path="/statistics/rating">
                    <Statistics />
                  </PrivateRoute>
                ),
              },
            ],
          },
          {
            path: 'chat',
            children: [
              {
                path: '',
                element: (
                  <PrivateRoute path="/chat">
                    <Chat />
                  </PrivateRoute>
                ),
              },
              {
                path: ':name',
                element: (
                  <PrivateRoute path="/chat/:name">
                    <Room />
                  </PrivateRoute>
                ),
              },
            ],
          },
          {
            path: 'releases',
            element: (
              <PrivateRoute path="/releases">
                <ReleaseNotes />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: '*',
        element: <Navigate replace to="/profile" />,
      },
    ],
  },
]
